// dashboard/widgets/TrackerPopout.js
// Tracker detail pop-out (weight/wellbeing/seizures), opened from a
// PetCard's mini widgets. Extracted from App.js (2026-08-12 split).

import i18n from '../../i18n';
import { useTranslation } from 'react-i18next';
import { c } from '../constants';
import { fmtWeight, toDisplayWeight } from '../utils';
import MiniLineChart from './MiniLineChart';
import { useModalA11y } from '../useModalA11y';

export default function TrackerPopout({ type, data, onClose, useLbs }) {
  const { t } = useTranslation();
  const items  = (data || []).slice(0, 6); // newest first
  const chrono = [...items].reverse();     // oldest first for charts
  const containerRef = useModalA11y(onClose);

  const wbColor = v => v === 'good' ? c.green  : v === 'difficult' ? c.red    : c.yellow;
  const wbBg    = v => v === 'good' ? c.greenLight : v === 'difficult' ? c.redLight : c.yellowLight;
  const wbLabel = v => v === 'good' ? t('patientCard.wbGood') : v === 'difficult' ? t('patientCard.wbDifficult') : t('patientCard.wbOkay');
  const fmtDt   = iso => new Date(iso).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', year: '2-digit' });

  const titles = { weight: t('patientCard.weightTrendTitle'), wellbeing: t('patientCard.wellbeingLogTitle'), seizures: t('patientCard.seizureHistoryTitle') };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.35)',
      zIndex: 80, display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div
        ref={containerRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tracker-popout-title"
        onClick={e => e.stopPropagation()}
        style={{
          backgroundColor: c.white, borderRadius: 12, padding: '20px 24px',
          width: 400, maxHeight: '80vh', overflowY: 'auto',
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          fontFamily: 'Inter, system-ui, sans-serif',
          outline: 'none',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <span id="tracker-popout-title" style={{ fontSize: 15, fontWeight: 700, color: c.slate }}>{titles[type]}</span>
          <button onClick={onClose} aria-label={t('common.close')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: c.textSoft, lineHeight: 1, padding: '0 4px' }}>×</button>
        </div>

        {/* Weight */}
        {type === 'weight' && (() => {
          const pts  = chrono.map(w => ({ t: new Date(w.measured_at + 'T12:00:00').getTime(), v: toDisplayWeight(w.weight, useLbs) }));
          const pct  = chrono.length >= 2
            ? ((parseFloat(chrono[chrono.length - 1].weight) - parseFloat(chrono[0].weight)) / parseFloat(chrono[0].weight)) * 100
            : null;
          const arrow = pct === null ? null : Math.abs(pct) <= 5 ? '→' : pct > 0 ? '↗' : '↘';
          const arrowCol = pct === null ? c.textSoft : Math.abs(pct) <= 5 ? c.green : pct > 0 ? c.yellow : c.red;
          return (
            <>
              {arrow && (
                <div style={{ fontSize: 22, fontWeight: 700, color: arrowCol, marginBottom: 10 }}>
                  {arrow} <span style={{ fontSize: 13, fontWeight: 600 }}>{pct >= 0 ? '+' : ''}{pct.toFixed(1)}%</span>
                </div>
              )}
              <MiniLineChart points={pts} lineColor={c.slate} />
              <div style={{ borderRadius: 8, overflow: 'hidden', border: `1px solid ${c.border}` }}>
                {items.map((w, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderBottom: i < items.length - 1 ? `1px solid ${c.border}` : 'none', backgroundColor: i === 0 ? c.fog : c.white }}>
                    <span style={{ fontSize: 13, fontWeight: i === 0 ? 600 : 400, color: c.slate }}>{fmtWeight(w.weight, useLbs)}</span>
                    <span style={{ fontSize: 12, color: c.textSoft }}>{fmtDt(w.measured_at + 'T12:00:00')}</span>
                  </div>
                ))}
              </div>
            </>
          );
        })()}

        {/* Wellbeing */}
        {type === 'wellbeing' && (() => {
          return (
            <>
              {/* Tile chart — oldest left to newest right */}
              <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
                {chrono.map((d, i) => (
                  <div key={i} style={{
                    flex: 1, padding: '8px 4px', borderRadius: 6, textAlign: 'center',
                    backgroundColor: wbBg(d.value), border: `1px solid ${wbColor(d.value)}33`,
                  }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: wbColor(d.value), textTransform: 'uppercase', letterSpacing: 0.3 }}>{wbLabel(d.value)}</div>
                    <div style={{ fontSize: 9, color: c.textSoft, marginTop: 3 }}>
                      {new Date(d.log_date + 'T12:00:00').toLocaleString(i18n.language, { month: 'short', day: 'numeric' })}
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ borderRadius: 8, overflow: 'hidden', border: `1px solid ${c.border}` }}>
                {items.map((d, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderBottom: i < items.length - 1 ? `1px solid ${c.border}` : 'none' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: wbColor(d.value) }}>{wbLabel(d.value)}</span>
                    <span style={{ fontSize: 12, color: c.textSoft }}>{fmtDt(d.log_date + 'T12:00:00')}</span>
                  </div>
                ))}
              </div>
            </>
          );
        })()}

        {/* Seizures */}
        {type === 'seizures' && (() => {
          return (
            <div style={{ borderRadius: 8, overflow: 'hidden', border: `1px solid ${c.border}` }}>
              {items.map((s, i) => {
                const older = items[i + 1];
                const gap   = older ? Math.round((new Date(s.occurred_at) - new Date(older.occurred_at)) / 86400000) : null;
                return (
                  <div key={i} style={{ padding: '10px 12px', borderBottom: i < items.length - 1 ? `1px solid ${c.border}` : 'none', backgroundColor: i === 0 ? c.fog : c.white }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 13, fontWeight: i === 0 ? 600 : 400, color: c.slate }}>
                        {new Date(s.occurred_at).toLocaleDateString(i18n.language, { month: 'long', day: 'numeric', year: 'numeric' })}
                      </span>
                      {i === 0 && <span style={{ fontSize: 10, color: c.textSoft, fontStyle: 'italic' }}>{t('patientCard.mostRecent')}</span>}
                    </div>
                    {gap !== null && (
                      <div style={{ fontSize: 11, color: c.textSoft, marginTop: 2 }}>{t('patientCard.daysSincePrevious', { count: gap })}</div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>
    </div>
  );
}
